import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { CONTACT_TELEGRAM_URL } from '../../config/brand';
import { Unit, Lesson } from '../../hooks/useCourseApi';
import { Curriculum } from '../course/Curriculum';
import {
  unitBrowseService,
  getStandaloneUnitImageUrl,
  standaloneDetailsToBrowseUnit,
} from '../../services/unitBrowseService';
import { unitCodeService } from '../../services/unitCodeService';
import { StandaloneUnitDetails, StandaloneUnitDto } from '../../types/unit';
import UnitEnrollmentPopup from './UnitEnrollmentPopup';
import SEOHead from '../common/SEOHead';
import { cardTheme } from '../../styles/cardTheme';
import { BRAND_FULL_NAME, BRAND_NAME, BRAND_TEACHER_NAME, SITE_URL } from '../../config/brand';
import {
  BookOpen,
  Play,
  CheckCircle,
  Video,
  Target,
  MessageCircle,
  ShoppingCart,
  Sparkles,
  Loader2,
} from 'lucide-react';

function mapLessonsForCurriculum(lessons: StandaloneUnitDetails['lessons'] | undefined): Lesson[] {
  return (lessons ?? []).map((lesson) => {
    const base = {
      id: lesson.id,
      lessonName: lesson.lessonName,
      titel: lesson.titel,
      order: lesson.order,
      active: lesson.active,
    };
    if (lesson.type === 'Quiz') {
      return { ...base, type: 'Quiz' as const, isQuizSubmitted: null };
    }
    if (lesson.type === 'Assignment') {
      return { ...base, type: 'Assignment' as const, isAssignmentSubmitted: null };
    }
    if (lesson.type === 'File') {
      return { ...base, type: 'File' as const, isFileOpened: null };
    }
    return { ...base, type: 'Video' as const, isCompleted: null };
  });
}

const UnitDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const location = useLocation();
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();

  const [unitDetails, setUnitDetails] = useState<StandaloneUnitDetails | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedSection, setSelectedSection] = useState<'curriculum' | 'overview'>('curriculum');
  const [isEnrolled, setIsEnrolled] = useState(false);
  const [isCheckingEnrollment, setIsCheckingEnrollment] = useState(true);
  const [showEnrollment, setShowEnrollment] = useState(false);

  const unitId = id ? parseInt(id, 10) : NaN;

  useEffect(() => {
    const load = async () => {
      if (!id || Number.isNaN(unitId)) return;
      setIsLoading(true);
      setError(null);

      const stateUnit = location.state?.unit as StandaloneUnitDto | StandaloneUnitDetails | undefined;
      if (stateUnit?.id === unitId) {
        setUnitDetails({
          id: stateUnit.id,
          unitName: stateUnit.unitName ?? '',
          titel: stateUnit.titel || ('unitTitle' in stateUnit ? stateUnit.unitTitle : '') || '',
          price: stateUnit.price ?? 0,
          imagePath: stateUnit.imagePath,
          teacherId: stateUnit.teacherId,
          active: stateUnit.active ?? true,
          order: stateUnit.order ?? 0,
          creationDate: 'creationDate' in stateUnit ? stateUnit.creationDate : undefined,
          lessons: Array.isArray((stateUnit as StandaloneUnitDetails).lessons)
            ? (stateUnit as StandaloneUnitDetails).lessons
            : [],
        });
      }

      const result = await unitBrowseService.getStandaloneUnitById(unitId);
      if (result.success) {
        setUnitDetails(result.data);
      } else if (!stateUnit) {
        // Check if user is enrolled and should use unit-viewer instead
        if (isAuthenticated) {
          try {
            const enrollmentCheck = await unitCodeService.getMyUnitCodes(1, 100);
            const enrolled = enrollmentCheck.success && 
                           enrollmentCheck.data?.units?.some((u) => u.unitId === unitId);
            
            if (enrolled) {
              console.log('User is enrolled in unit', unitId, '- redirecting to viewer');
              // User is enrolled - redirect to unit viewer
              navigate(`/unit-viewer/${unitId}`, { replace: true });
              return;
            }
          } catch (err) {
            console.error('Error checking enrollment:', err);
          }
        }
        
        setError(result.error);
      }
      setIsLoading(false);
    };
    load();
  }, [id, unitId, location.state, isAuthenticated, navigate]);

  useEffect(() => {
    const checkEnrollment = async () => {
      if (!isAuthenticated || Number.isNaN(unitId)) {
        setIsCheckingEnrollment(false);
        return;
      }
      setIsCheckingEnrollment(true);
      try {
        const response = await unitCodeService.getMyUnitCodes(1, 100);
        const enrolled = Boolean(
          response.success && response.data?.units?.some((u) => u.unitId === unitId)
        );
        setIsEnrolled(enrolled);
      } catch {
        setIsEnrolled(false);
      } finally {
        setIsCheckingEnrollment(false);
      }
    };
    checkEnrollment();
  }, [isAuthenticated, unitId]);

  const curriculumUnit: Unit | null = useMemo(() => {
    if (!unitDetails) return null;
    return {
      id: unitDetails.id,
      unitName: unitDetails.unitName,
      titel: unitDetails.titel,
      active: unitDetails.active,
      order: unitDetails.order,
      lessons: mapLessonsForCurriculum(unitDetails.lessons),
    };
  }, [unitDetails]);

  const displayTitle = unitDetails?.titel || unitDetails?.unitName || '';
  const totalLessons = unitDetails?.lessons?.length ?? 0;
  const isFree = (unitDetails?.price ?? 0) === 0;
  const formattedPrice = (unitDetails?.price ?? 0).toLocaleString('ar-EG');

  const handleContinue = () => navigate(`/unit-viewer/${unitId}`);

  const handleCheckout = () => {
    if (!unitDetails) return;
    const unit = standaloneDetailsToBrowseUnit(unitDetails);
    if (!isAuthenticated) {
      navigate('/login', {
        state: { redirectTo: '/checkout', unit, unitId: unit.id },
      });
      return;
    }
    navigate('/checkout', { state: { unit, unitId: unit.id } });
  };

  const handleSupportContact = () => {
    window.open(CONTACT_TELEGRAM_URL, '_blank', 'noopener,noreferrer');
  };

  const handleSubscribe = () => {
    if (!unitDetails) return;
    if (!isAuthenticated) {
      navigate('/login', {
        state: {
          redirectTo: `/units/${unitId}`,
          unit: standaloneDetailsToBrowseUnit(unitDetails),
        },
      });
      return;
    }
    if (isFree) {
      setShowEnrollment(true);
      return;
    }
    setShowEnrollment(true);
  };

  const tabs = [
    { id: 'curriculum' as const, label: 'محتوى الحصة', icon: BookOpen },
    { id: 'overview' as const, label: 'نظرة عامة', icon: Target },
  ];

  if (isLoading) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center bg-gradient-to-b from-green-50/40 via-white to-gold-50/30 px-4">
        <div className="text-center">
          <Loader2 className="mx-auto mb-4 h-10 w-10 animate-spin text-green-700" />
          <p className="text-sm font-medium text-gray-600 sm:text-base">جاري تحميل تفاصيل الحصة...</p>
        </div>
      </div>
    );
  }

  if (error || !unitDetails) {
    console.log('UnitDetail Error State:', { error, hasUnitDetails: !!unitDetails, unitId });
    
    return (
      <div className="flex min-h-[60vh] items-center justify-center bg-gradient-to-b from-green-50/40 via-white to-gold-50/30 px-4">
        <div className={`${cardTheme.shell} max-w-md p-8 text-center`}>
          <div className="mb-4 text-4xl">🔍</div>
          <p className={`mb-4 text-lg font-bold ${cardTheme.title}`}>تعذّر تحميل الحصة</p>
          <p className="mb-6 text-sm text-gray-600">{error || 'الحصة غير متاحة حالياً'}</p>
          
          {/* Add helpful suggestions */}
          <div className="mb-6 rounded-lg bg-blue-50 p-4 text-right">
            <p className="mb-2 text-xs font-semibold text-blue-900">💡 اقتراحات:</p>
            <ul className="space-y-1 text-xs text-blue-800">
              <li>• تأكد من رقم الحصة</li>
              <li>• إذا كنت مشترك، جرّب <button onClick={() => navigate(`/unit-viewer/${unitId}`)} className="font-bold underline">صفحة المشاهدة</button></li>
              <li>• تواصل مع الدعم إذا استمرت المشكلة</li>
            </ul>
          </div>
          
          <div className="flex flex-col gap-3 sm:flex-row sm:justify-center">
            <button
              type="button"
              onClick={() => navigate('/student-portal')}
              className={`rounded-xl px-6 py-2.5 ${cardTheme.btnPrimary}`}
            >
              العودة للحصص والكورسات
            </button>
            <button
              type="button"
              onClick={() => window.open(CONTACT_TELEGRAM_URL, '_blank')}
              className={`rounded-xl px-6 py-2.5 ${cardTheme.btnOutline}`}
            >
              <MessageCircle className="inline h-4 w-4 ml-1" />
              تواصل مع الدعم
            </button>
          </div>
        </div>
      </div>
    );
  }

  const imageUrl = getStandaloneUnitImageUrl(standaloneDetailsToBrowseUnit(unitDetails));
  const unitPath = `/units/${unitDetails.id}`;
  const unitSeoDescription = `حصة ${displayTitle} على منصة نهج الأزهر مع الأستاذ سعيد الفقي — محتوى تعليمي أزهري أونلاين${
    totalLessons ? ` يشمل ${totalLessons} درس` : ''
  }.`;

  return (
    <div className="min-h-screen bg-gradient-to-b from-green-50/40 via-white to-gold-50/30 font-cairo" dir="rtl">
      <SEOHead
        title={`${displayTitle || 'حصة مستقلة'} | ${BRAND_NAME} | ${BRAND_TEACHER_NAME}`}
        description={unitSeoDescription}
        keywords={`${displayTitle}, حصة مستقلة, ${BRAND_NAME}, ${BRAND_TEACHER_NAME}, دروس أزهرية, تعليم أونلاين`}
        url={unitPath}
        image={imageUrl}
        type="product"
        breadcrumbs={[{ name: displayTitle || 'حصة مستقلة', path: unitPath }]}
        structuredData={{
          '@context': 'https://schema.org',
          '@type': 'Course',
          name: displayTitle,
          description: unitSeoDescription,
          url: `${SITE_URL}${unitPath}`,
          image: imageUrl.startsWith('http') ? imageUrl : `${SITE_URL}${imageUrl}`,
          provider: {
            '@type': 'EducationalOrganization',
            name: BRAND_FULL_NAME,
            url: SITE_URL,
          },
          offers: {
            '@type': 'Offer',
            price: unitDetails.price ?? 0,
            priceCurrency: 'EGP',
            availability: 'https://schema.org/InStock',
            url: `${SITE_URL}${unitPath}`,
          },
        }}
      />
      <div className="mx-auto max-w-6xl px-4 py-6 sm:px-6 sm:py-8 lg:px-8 lg:py-10">
        <section className={`${cardTheme.shell} overflow-hidden`}>
          <div className={`${cardTheme.accentTop} p-5 sm:p-6 lg:p-8`}>
            <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_minmax(260px,340px)] lg:items-start lg:gap-8">
              <div className="order-2 lg:order-1">
                <div className="mb-4 flex flex-wrap items-center justify-center gap-2 lg:justify-start">
                  <span className={cardTheme.badgeGold}>
                    <Sparkles className="h-3.5 w-3.5" />
                    حصة مستقلة
                  </span>
                  {isFree && (
                    <span className="inline-flex items-center rounded-full bg-gold-500 px-3 py-1 text-xs font-bold text-green-950">
                      مجاني
                    </span>
                  )}
                </div>

                <h1 className={`mb-3 text-2xl font-bold leading-tight sm:text-3xl lg:text-4xl ${cardTheme.title}`}>
                  {displayTitle}
                </h1>
                {unitDetails.unitName && unitDetails.unitName !== displayTitle && (
                  <p className="mb-4 text-sm text-gray-500 sm:text-base">{unitDetails.unitName}</p>
                )}

                <div className="mb-6 flex flex-wrap items-center justify-center gap-3 lg:justify-start">
                  <div className="flex items-center gap-2 rounded-xl border border-green-100 bg-green-50/60 px-3 py-2">
                    <Video className="h-4 w-4 text-green-700" />
                    <span className="text-sm font-semibold text-green-900">{totalLessons} درس</span>
                  </div>
                  <div className="flex items-center gap-2 rounded-xl border border-gold-200 bg-gold-50/60 px-3 py-2">
                    <span className={`text-sm font-bold ${cardTheme.price}`}>
                      {isFree ? 'مجاناً' : `${formattedPrice} جنيه`}
                    </span>
                  </div>
                </div>

                <div className="space-y-3">
                  {isCheckingEnrollment ? (
                    <div className="flex items-center justify-center gap-2 py-2 lg:justify-start">
                      <Loader2 className="h-5 w-5 animate-spin text-green-700" />
                      <span className="text-sm text-gray-600">جاري التحقق من الاشتراك...</span>
                    </div>
                  ) : isEnrolled ? (
                    <button
                      type="button"
                      onClick={handleContinue}
                      className={`inline-flex min-h-11 w-full items-center justify-center gap-2 rounded-xl px-6 py-3 sm:w-auto ${cardTheme.btnPrimary}`}
                    >
                      <Play className="h-4 w-4" />
                      متابعة الحصة
                    </button>
                  ) : (
                    <div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap">
                      <button
                        type="button"
                        onClick={handleSubscribe}
                        className={`inline-flex min-h-11 flex-1 items-center justify-center gap-2 rounded-xl px-6 py-3 sm:flex-none sm:min-w-[160px] ${cardTheme.btnPrimary}`}
                      >
                        <ShoppingCart className="h-4 w-4" />
                        {isFree ? 'اشترك مجاناً' : 'اشترك الآن'}
                      </button>
                      {!isFree && (
                        <button
                          type="button"
                          onClick={handleCheckout}
                          className={`inline-flex min-h-11 flex-1 items-center justify-center gap-2 rounded-xl px-6 py-3 sm:flex-none ${cardTheme.btnOutline}`}
                        >
                          الدفع الإلكتروني
                        </button>
                      )}
                      <button
                        type="button"
                        onClick={handleSupportContact}
                        className={`inline-flex min-h-11 flex-1 items-center justify-center gap-2 rounded-xl px-6 py-3 sm:flex-none ${cardTheme.btnOutline}`}
                      >
                        <MessageCircle className="h-4 w-4" />
                        تواصل مع الإشراف عبر تيليجرام
                      </button>
                    </div>
                  )}
                </div>
              </div>

              <div className="order-1 lg:order-2">
                <div className="relative overflow-hidden rounded-2xl border border-green-100 bg-green-50 shadow-md">
                  <div className="aspect-[16/10] w-full">
                    <img
                      src={imageUrl}
                      alt={displayTitle}
                      className="h-full w-full object-cover"
                      onError={(e) => {
                        e.currentTarget.src = '/default-course.jpg';
                      }}
                    />
                  </div>
                  <div className="absolute inset-0 bg-gradient-to-t from-green-950/50 via-transparent to-transparent" />
                  <div className="absolute bottom-3 right-3 left-3">
                    <span className="inline-flex items-center gap-1.5 rounded-full border border-white/20 bg-black/30 px-3 py-1 text-xs font-semibold text-white backdrop-blur-sm">
                      <Video className="h-3.5 w-3.5" />
                      {totalLessons} درس تعليمي
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="mt-6 sm:mt-8">
          <div className="mb-5 flex justify-center">
            <div className="inline-flex rounded-xl border border-green-100 bg-white p-1 shadow-sm">
              {tabs.map((tab) => {
                const Icon = tab.icon;
                const active = selectedSection === tab.id;
                return (
                  <button
                    key={tab.id}
                    type="button"
                    onClick={() => setSelectedSection(tab.id)}
                    className={`inline-flex min-h-10 items-center gap-1.5 rounded-lg px-4 py-2 text-xs font-semibold transition-all sm:gap-2 sm:px-5 sm:text-sm ${
                      active
                        ? 'bg-green-800 text-white shadow-sm'
                        : 'text-gray-600 hover:bg-green-50 hover:text-green-800'
                    }`}
                  >
                    <Icon className="h-4 w-4" />
                    {tab.label}
                  </button>
                );
              })}
            </div>
          </div>

          <div className={`${cardTheme.shell} p-5 sm:p-6 lg:p-8`}>
            {selectedSection === 'curriculum' ? (
              <div>
                <div className="mb-6 text-center sm:mb-8">
                  <h2 className={`mb-2 text-xl font-bold sm:text-2xl ${cardTheme.title}`}>محتوى الحصة</h2>
                  <p className="text-sm text-gray-600 sm:text-base">{totalLessons} درس في هذه الحصة</p>
                  <div className={`mx-auto mt-3 h-1 w-12 rounded-full ${cardTheme.divider}`} />
                </div>
                {curriculumUnit && (
                  <Curriculum units={[curriculumUnit]} courseId={String(unitDetails.id)} />
                )}
              </div>
            ) : (
              <div className="mx-auto max-w-4xl">
                <div className="mb-6 text-center sm:mb-8">
                  <h2 className={`mb-2 text-xl font-bold sm:text-2xl ${cardTheme.title}`}>نظرة عامة</h2>
                  <p className="text-sm text-gray-600 sm:text-base">تفاصيل الحصة ومحتواها</p>
                  <div className={`mx-auto mt-3 h-1 w-12 rounded-full ${cardTheme.divider}`} />
                </div>

                <div className={`mb-6 rounded-xl border border-green-100 ${cardTheme.feature} p-5 sm:p-6`}>
                  <h3 className="mb-3 flex items-center gap-2 text-base font-bold text-green-900 sm:text-lg">
                    <Target className="h-5 w-5 text-gold-600" />
                    عن الحصة
                  </h3>
                  <p className="text-sm leading-relaxed text-gray-700 sm:text-base">
                    {displayTitle} — حصة مستقلة تحتوي على {totalLessons} درس تعليمي يمكنك الاشتراك فيها
                    مباشرة دون الحاجة لشراء كورس كامل.
                  </p>
                </div>

                <div className={`rounded-xl border border-green-100 p-5 ${cardTheme.feature}`}>
                  <h3 className="mb-4 flex items-center gap-2 text-base font-bold text-green-900">
                    <CheckCircle className="h-5 w-5 text-green-700" />
                    تفاصيل الحصة
                  </h3>
                  <ul className="space-y-3 text-sm text-gray-700">
                    <li className="flex items-center gap-2">
                      <CheckCircle className="h-4 w-4 shrink-0 text-gold-600" />
                      عدد الدروس: {totalLessons}
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckCircle className="h-4 w-4 shrink-0 text-gold-600" />
                      السعر: {isFree ? 'مجاني' : `${formattedPrice} جنيه`}
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckCircle className="h-4 w-4 shrink-0 text-gold-600" />
                      نوع المحتوى: حصة مستقلة
                    </li>
                  </ul>
                </div>
              </div>
            )}
          </div>
        </section>

        <section className="mt-6 rounded-2xl border border-green-100 bg-white p-6 text-center sm:mt-8 sm:p-8">
          <h2 className={`mb-2 text-lg font-bold sm:text-xl ${cardTheme.title}`}>عندك سؤال عن الحصة؟</h2>
          <p className="mb-5 text-sm text-gray-600 sm:text-base">فريقنا جاهز يساعدك عبر تيليجرام</p>
          <button
            type="button"
            onClick={handleSupportContact}
            className={`inline-flex min-h-11 items-center gap-2 rounded-xl px-6 py-3 ${cardTheme.btnOutline}`}
          >
            <MessageCircle className="h-4 w-4" />
            تواصل عبر تيليجرام
          </button>
        </section>
      </div>

      {unitDetails && (
        <UnitEnrollmentPopup
          isOpen={showEnrollment}
          onClose={() => setShowEnrollment(false)}
          unit={standaloneDetailsToBrowseUnit(unitDetails)}
          onSuccess={() => {
            setShowEnrollment(false);
            setIsEnrolled(true);
          }}
        />
      )}
    </div>
  );
};

export default UnitDetail;
